/**
 * JourneyRoomsDesignOverlay — sidebar for Journey Rooms Design Mode.
 *
 * Sections:
 *   • Selected element — dx/dy/rot/scl editors + Delete + Reset
 *   • Add new element — Votive / Vessel / Rug
 *   • Elements inventory — all built-ins + custom items (click to select)
 *   • Hidden — restore removed built-ins
 *   • Global — Grid toggle · Reset all · Copy layout JSON
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getLayout,
  resetAll,
  resetOne,
  setOffset,
  updateCustom,
  deleteElement,
  removeCustom,
  isCustomId,
  setHidden,
  subscribe,
} from "@/lib/designMode";
import { Copy, Grid3x3, RotateCcw, X, Plus, Trash2, Eye } from "lucide-react";

const FIELD_LABELS = { dx: "X (px)", dy: "Y (px)", rot: "Rotate (°)", scl: "Scale" };
const KIND_LABELS = { votive: "Votive candle", vessel: "Candle vessel", rug: "Area rug" };

export default function JourneyRoomsDesignOverlay({
  selectedId,
  onSelect,
  gridOn,
  onToggleGrid,
  builtInInventory,
  onAdd,
}) {
  const [layout, setLayout] = useState(getLayout());
  const [copied, setCopied] = useState(false);

  useEffect(() => subscribe(setLayout), []);

  const isCustom = selectedId && isCustomId(selectedId);
  const customItem = isCustom ? layout.custom.find((c) => c.id === selectedId) : null;
  const builtInOffset = !isCustom && selectedId ? layout.offsets[selectedId] || {} : null;

  const hiddenIds = useMemo(() => Object.keys(layout.hidden || {}), [layout]);
  const nonIdentityCount =
    Object.keys(layout.offsets || {}).length +
    hiddenIds.length +
    (layout.custom || []).length;

  const copyLayout = async () => {
    const json = JSON.stringify(layout, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Layout JSON copied to clipboard");
    } catch {
      // eslint-disable-next-line no-console
      console.log("Journey Rooms layout:\n" + json);
      toast.info("Clipboard unavailable — layout logged to console");
    }
  };

  const updateSelectedField = (k, v) => {
    if (!selectedId) return;
    const num = Number(v);
    const val = Number.isFinite(num) ? num : 0;
    if (isCustom) {
      if (k === "dx") updateCustom(selectedId, { x: (customItem?.x || 0) + val - (customItem?.x || 0) });
      // For custom elements, dx/dy don't apply — we edit x/y directly. Show a
      // simpler set of fields below.
      updateCustom(selectedId, { [k === "dx" ? "x" : k === "dy" ? "y" : k]: val });
    } else {
      setOffset(selectedId, { [k]: val });
    }
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    deleteElement(selectedId);
    onSelect(null);
    toast.success(isCustom ? "Deleted custom element" : "Hidden built-in element");
  };

  return (
    <aside
      className="fixed right-4 top-24 z-[80] w-[320px] rounded-2xl border border-stone-700/60 bg-stone-900/95 text-stone-100 shadow-2xl backdrop-blur-md"
      data-testid="journey-design-overlay"
    >
      <header className="flex items-center justify-between border-b border-stone-700/60 px-4 py-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/80">Journey Rooms</div>
          <div className="text-sm font-medium">Design Mode</div>
        </div>
        <button
          className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-800 hover:text-stone-100"
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete("design");
            window.location.href = url.toString();
          }}
          title="Exit Design Mode"
          data-testid="journey-design-exit"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="max-h-[calc(100vh-180px)] space-y-4 overflow-y-auto px-4 py-4 text-sm">
        {/* ── Selected element ─────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.14em] text-stone-400">Selected element</div>
          {selectedId ? (
            <div className="rounded-lg bg-stone-800/70 p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <code className="rounded bg-stone-950/60 px-1.5 py-0.5 text-[11px] text-amber-200">{selectedId}</code>
                <div className="flex items-center gap-2">
                  {!isCustom && (
                    <button
                      className="text-[11px] text-stone-400 hover:text-stone-100"
                      onClick={() => {
                        resetOne(selectedId);
                        toast.success(`Reset ${selectedId}`);
                      }}
                      data-testid="journey-design-reset-selected"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-rose-300 hover:bg-rose-950/40 hover:text-rose-200"
                    onClick={deleteSelected}
                    data-testid="journey-design-delete"
                    title={isCustom ? "Remove custom element" : "Hide this built-in element"}
                  >
                    <Trash2 className="h-3 w-3" />
                    {isCustom ? "Delete" : "Hide"}
                  </button>
                </div>
              </div>
              {isCustom ? (
                <>
                  <div className="mb-2 text-[10px] uppercase tracking-wider text-stone-500">
                    {KIND_LABELS[customItem?.kind] || "Custom"}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">X (px)</span>
                      <input
                        type="number"
                        step={1}
                        value={customItem?.x ?? 0}
                        onChange={(e) => updateCustom(selectedId, { x: Number(e.target.value) || 0 })}
                        className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                        data-testid="journey-design-custom-x"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">Y (px)</span>
                      <input
                        type="number"
                        step={1}
                        value={customItem?.y ?? 0}
                        onChange={(e) => updateCustom(selectedId, { y: Number(e.target.value) || 0 })}
                        className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                        data-testid="journey-design-custom-y"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">Rotate (°)</span>
                      <input
                        type="number"
                        step={1}
                        value={customItem?.rot ?? 0}
                        onChange={(e) => updateCustom(selectedId, { rot: Number(e.target.value) || 0 })}
                        className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">Scale</span>
                      <input
                        type="number"
                        step={0.05}
                        value={customItem?.scl ?? 1}
                        onChange={(e) => updateCustom(selectedId, { scl: Number(e.target.value) || 1 })}
                        className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                      />
                    </label>
                    {customItem?.kind === "rug" && (
                      <>
                        <label className="block">
                          <span className="text-[10px] uppercase tracking-wider text-stone-400">Width</span>
                          <input
                            type="number"
                            step={1}
                            value={customItem?.w ?? 100}
                            onChange={(e) => updateCustom(selectedId, { w: Number(e.target.value) || 100 })}
                            className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                          />
                        </label>
                        <label className="block">
                          <span className="text-[10px] uppercase tracking-wider text-stone-400">Height</span>
                          <input
                            type="number"
                            step={1}
                            value={customItem?.h ?? 60}
                            onChange={(e) => updateCustom(selectedId, { h: Number(e.target.value) || 60 })}
                            className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                          />
                        </label>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {["dx", "dy", "rot", "scl"].map((k) => (
                    <label key={k} className="block">
                      <span className="text-[10px] uppercase tracking-wider text-stone-400">{FIELD_LABELS[k]}</span>
                      <input
                        type="number"
                        step={k === "scl" ? 0.05 : 1}
                        value={builtInOffset?.[k] ?? (k === "scl" ? 1 : 0)}
                        onChange={(e) => setOffset(selectedId, { [k]: Number(e.target.value) || 0 })}
                        className="mt-0.5 w-full rounded border border-stone-700 bg-stone-950/60 px-2 py-1 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                        data-testid={`journey-design-input-${k}`}
                      />
                    </label>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[10px] leading-relaxed text-stone-500">
                Drag to move · Arrows nudge 1px (Shift = 10px) · Alt+←/→ rotates · Del removes · Esc deselects
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-stone-800/40 p-3 text-[12px] text-stone-400">
              Click any candle, vessel or rug in the hallway to select it, then drag or use arrow keys.
            </div>
          )}
        </section>

        {/* ── Add new element ─────────────────────────────────────── */}
        <section className="space-y-2">
          <div className="text-[11px] uppercase tracking-[0.14em] text-stone-400">Add new element</div>
          <div className="grid grid-cols-3 gap-2">
            {["votive", "vessel", "rug"].map((k) => (
              <button
                key={k}
                onClick={() => onAdd(k)}
                className="flex flex-col items-center gap-1 rounded-md border border-stone-700 px-2 py-2 text-[11px] text-stone-200 transition hover:border-amber-500/60 hover:bg-amber-500/10"
                data-testid={`journey-design-add-${k}`}
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="capitalize">{k}</span>
              </button>
            ))}
          </div>
          <div className="text-[10px] leading-relaxed text-stone-500">
            New elements appear near the top-left of the hallway — drag them into place.
          </div>
        </section>

        {/* ── Inventory: built-ins + custom ────────────────────────── */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.14em] text-stone-400">
              Elements ({builtInInventory.length + (layout.custom || []).length})
            </div>
            <div className="text-[10px] text-stone-500">{nonIdentityCount} modified</div>
          </div>
          <ul className="max-h-56 space-y-1 overflow-y-auto pr-1">
            {builtInInventory.map((id) => {
              const off = layout.offsets[id];
              const hidden = layout.hidden[id];
              const isSel = selectedId === id;
              return (
                <li key={id}>
                  <button
                    className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-[12px] transition ${
                      isSel
                        ? "bg-amber-500/20 text-amber-100"
                        : hidden
                          ? "text-stone-600 line-through hover:bg-stone-800"
                          : "text-stone-300 hover:bg-stone-800"
                    }`}
                    onClick={() => onSelect(id)}
                    data-testid={`journey-design-select-${id}`}
                  >
                    <code className="text-[11px]">{id}</code>
                    {hidden ? (
                      <span className="text-[10px] text-rose-400">hidden</span>
                    ) : off ? (
                      <span className="text-[10px] text-stone-400">
                        {off.dx || 0},{off.dy || 0}
                        {off.rot ? ` ${off.rot}°` : ""}
                        {off.scl && off.scl !== 1 ? ` ×${off.scl}` : ""}
                      </span>
                    ) : (
                      <span className="text-[10px] text-stone-600">—</span>
                    )}
                  </button>
                </li>
              );
            })}
            {(layout.custom || []).map((c) => {
              const isSel = selectedId === c.id;
              return (
                <li key={c.id}>
                  <button
                    className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-[12px] transition ${
                      isSel ? "bg-amber-500/20 text-amber-100" : "text-stone-300 hover:bg-stone-800"
                    }`}
                    onClick={() => onSelect(c.id)}
                    data-testid={`journey-design-select-${c.id}`}
                  >
                    <code className="text-[11px]">
                      <span className="text-emerald-400">+</span> {c.id} <span className="text-stone-500">({c.kind})</span>
                    </code>
                    <span className="text-[10px] text-stone-400">
                      {c.x},{c.y}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ── Hidden built-ins ────────────────────────────────────── */}
        {hiddenIds.length > 0 && (
          <section className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.14em] text-stone-400">
              Hidden ({hiddenIds.length})
            </div>
            <ul className="space-y-1">
              {hiddenIds.map((id) => (
                <li key={id} className="flex items-center justify-between rounded bg-stone-800/40 px-2 py-1">
                  <code className="text-[11px] text-stone-400">{id}</code>
                  <button
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-emerald-300 hover:bg-emerald-950/40 hover:text-emerald-200"
                    onClick={() => {
                      setHidden(id, false);
                      toast.success(`Restored ${id}`);
                    }}
                    data-testid={`journey-design-restore-${id}`}
                  >
                    <Eye className="h-3 w-3" />
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Global actions ──────────────────────────────────────── */}
        <section className="grid grid-cols-2 gap-2">
          <button
            className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-[12px] transition ${
              gridOn
                ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                : "border-stone-700 text-stone-300 hover:bg-stone-800"
            }`}
            onClick={onToggleGrid}
            data-testid="journey-design-grid-toggle"
          >
            <Grid3x3 className="h-3.5 w-3.5" />
            {gridOn ? "Grid on" : "Grid off"}
          </button>
          <button
            className="flex items-center justify-center gap-1.5 rounded-md border border-stone-700 px-2 py-2 text-[12px] text-stone-300 transition hover:bg-stone-800"
            onClick={() => {
              if (window.confirm("Reset all Journey Rooms decorative offsets, custom elements, and hidden flags?")) {
                resetAll();
                onSelect(null);
                toast.success("Layout reset");
              }
            }}
            data-testid="journey-design-reset-all"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset all
          </button>
          <button
            className="col-span-2 flex items-center justify-center gap-1.5 rounded-md bg-amber-500/90 px-2 py-2 text-[12px] font-medium text-stone-950 transition hover:bg-amber-400"
            onClick={copyLayout}
            data-testid="journey-design-copy"
          >
            <Copy className="h-3.5 w-3.5" />
            {copied ? "Copied!" : "Copy layout JSON"}
          </button>
        </section>
      </div>
    </aside>
  );
}
